import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiGrid,
  FiMessageCircle,
  FiUser,
  FiLogOut,
  FiChevronDown,
  FiFolder,
  FiMenu,
  FiX,
  FiChevronLeft,
} from "react-icons/fi";

import { HiOutlineSwitchVertical } from "react-icons/hi";
import logoName from "../../images/auravit_logo.png";
import auravitIcon from "../../images/auraviticon.png";
import cfsiLogo from "../../images/CFSI-mobile-logo-sr.png";


import PeerDashboard from "./PeerDashboard";
import PeerChats from "./PeerChats";
import PeerChatform from "./PeerChatform";
import PeerProfile from "./PeerProfile";
import PeerResources from "./PeerResources";
import StaffLogoutModal from "./StaffLogoutModal";
import { useLanguage } from "../../contexts/LanguageContext";
import "./staffcss/peereducator.css";
import { auth, db } from "../../firebase";
import { doc, updateDoc, onSnapshot } from "firebase/firestore";
import { switchRoleWithUpdate, roleConfigs } from "../../utils/roleSwitching";
import StaffChangePassword from "../../components/staffcomponents/StaffChangePassword";
import StaffChangeVerificationModal from "../../components/otpmodals/StaffChangeVerificationModal";



const PeerEducator = () => {
  const navigate = useNavigate();
  const [activePanel, setActivePanel] = useState("dashboard");
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const [showCannotLogoutModal, setShowCannotLogoutModal] = useState(false);
  const [activeChatsCount, setActiveChatsCount] = useState(0);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
  const [tooltip, setTooltip] = useState({ visible: false, text: '', x: 0, y: 0 });
  
  // Onboarding state
  const [showPasswordChangeModal, setShowPasswordChangeModal] = useState(false);
  const [showLanguageSetupModal, setShowLanguageSetupModal] = useState(false);
  const [showPasswordVerificationModal, setShowPasswordVerificationModal] = useState(false);
  const [pendingPasswordChange, setPendingPasswordChange] = useState(null);
  const [userData, setUserData] = useState(null);
  const [languageSpoken, setLanguageSpoken] = useState([]);
  const [tempNewLanguage, setTempNewLanguage] = useState("");
  
  const defaultLanguages = ["english", "tagalog", "bisaya"];
  
  // Peer Educator status state
  const [peerStatus, setPeerStatus] = useState("Ready"); // "Ready", "Away"
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  
  // Switch account dropdown state
  const [showRoleDropdown, setShowRoleDropdown] = useState(false);
  const [currentUserRole, setCurrentUserRole] = useState("");
  const [availableRoles, setAvailableRoles] = useState([]);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const switchButtonRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (switchButtonRef.current && !switchButtonRef.current.contains(event.target) && 
          !event.target.closest('.pe-sidebar-role-dropdown')) {
        setShowRoleDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle role switching
  const handleRoleSwitch = (selectedRole) => {
    const switched = switchRoleWithUpdate(selectedRole.id, currentUserRole, setCurrentUserRole, navigate);
    if (switched) {
      setShowRoleDropdown(false);
    }
  };

  // Calculate dropdown position for collapsed sidebar
  const handleDropdownToggle = () => {
    if (!showRoleDropdown && sidebarCollapsed && isDesktop && switchButtonRef.current) {
      const rect = switchButtonRef.current.getBoundingClientRect();
      const dropdownWidth = 200; // estimated width
      const dropdownHeight = 150; // estimated height
      
      // Calculate position to center dropdown above button
      let top = rect.top - dropdownHeight - 8;
      let left = rect.left + (rect.width / 2) - (dropdownWidth / 2);
      
      // Ensure dropdown doesn't go off-screen horizontally
      if (left < 10) left = 10;
      if (left + dropdownWidth > window.innerWidth - 10) {
        left = window.innerWidth - dropdownWidth - 10;
      }
      
      // Ensure dropdown doesn't go off-screen vertically
      if (top < 10) top = rect.bottom + 8; // Show below if above would be off-screen
      
      setDropdownPosition({ top, left });
    }
    setShowRoleDropdown(!showRoleDropdown);
  };

  // Desktop detection
  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Tooltip handlers
  const handleMouseEnter = (e, text) => {
    if (isDesktop && sidebarCollapsed) {
      const tooltipX = e.clientX + 12;
      const tooltipY = e.clientY;
      
      // Prevent tooltip from causing scroll by ensuring it stays within viewport
      const tooltipHeight = 40; // estimated height
      const viewportHeight = window.innerHeight;
      const adjustedY = Math.min(tooltipY, viewportHeight - tooltipHeight - 10);
      
      setTooltip({
        visible: true,
        text: text,
        x: tooltipX,
        y: adjustedY
      });
    }
  };

  const handleMouseMove = (e) => {
    if (tooltip.visible && isDesktop && sidebarCollapsed) {
      const tooltipX = e.clientX + 12;
      const tooltipY = e.clientY;
      
      // Prevent tooltip from causing scroll by ensuring it stays within viewport
      const tooltipHeight = 40; // estimated height
      const viewportHeight = window.innerHeight;
      const adjustedY = Math.min(tooltipY, viewportHeight - tooltipHeight - 10);
      
      setTooltip(prev => ({
        ...prev,
        x: tooltipX,
        y: adjustedY
      }));
    }
  };

  const handleMouseLeave = () => {
    setTooltip({ visible: false, text: '', x: 0, y: 0 });
  };

  // Single real-time listener for user data (consolidates 3 separate reads into 1 efficient listener)
  // Handles: role fetching, onboarding checks, peer status, and active chats count
  // Uses auth state listener to properly handle login/logout without redundancy
  useEffect(() => {
    const unsubscribeAuth = auth.onAuthStateChanged((user) => {
      if (!user) {
        // Clear user data when logged out
        setUserData(null);
        setAvailableRoles([]);
        setCurrentUserRole("");
        setPeerStatus("Ready");
        return;
      }

      const userDocRef = doc(db, "users", user.uid);
      let hasInitialized = false; // Track if we've performed one-time initialization
      
      const unsubscribeDoc = onSnapshot(userDocRef, (docSnapshot) => {
        if (docSnapshot.exists()) {
          const userData = docSnapshot.data();
          setUserData(userData);
          
          // Process roles
          let roles = userData.roles || [];
          
          // If user is a Peer Educator and servicesAssigned is true, add Medical Professional to the role list
          if (roles.includes("Peer Educator") && 
              (userData.servicesAssigned === true || userData.servicesassigned === true) && 
              !roles.includes("Medical Professional")) {
            roles = [...roles, "Medical Professional"];
          }


          
          // Filter available roles based on user's assigned roles
          const filteredRoles = roles
            .filter(roleId => roleConfigs[roleId])
            .map(roleId => ({
              id: roleId,
              name: roleConfigs[roleId].name,
              component: roleConfigs[roleId].component
            }));
          
          setAvailableRoles(filteredRoles);
          
          // Set current role based on current page or first available role
          const currentPath = window.location.pathname;
          let currentRole = "";
          
          if (currentPath.includes("/medical-professional")) {
            currentRole = "Medical Professional";
          } else if (currentPath.includes("/peer-educator")) {
            currentRole = "Peer Educator";
          } else if (currentPath.includes("/content-curator")) {
            currentRole = "Content Curator";
          } else {
            // Default to first available role if not on a staff page
            currentRole = filteredRoles.length > 0 ? filteredRoles[0].id : "";
          }
          
          setCurrentUserRole(currentRole);
          
          // Handle Peer Educator specific logic
          if (roles.includes("Peer Educator")) {
            // Update peer status and active chats count
            let loadedPeerStatus = userData.peerStatus || "Ready";
            if (!["Ready", "Away"].includes(loadedPeerStatus)) {
              loadedPeerStatus = "Ready";
            }
            
            setPeerStatus(loadedPeerStatus);
            
            // Only perform initialization writes on first snapshot to avoid unnecessary Firestore writes
            // on every real-time update. This significantly reduces write operations and costs.
            if (!hasInitialized) {
              // Check if fields need to be updated (missing fields or wrong values)
              const needsUpdate = 
                !userData.peerStatus || 
                !["Ready", "Away"].includes(userData.peerStatus) ||
                userData.peerStatus !== loadedPeerStatus;
              
              // Only write if actually needed
              if (needsUpdate) {
                updateDoc(doc(db, "users", user.uid), {
                  peerStatus: loadedPeerStatus
                }).catch(error => {
                  console.error("Error setting default peer status/activeChats:", error);
                });
              }
              
              // Check onboarding status (only on first snapshot)
              if (userData.passwordChanged !== true) {
                setShowPasswordChangeModal(true);
              } else if (!userData.languageSpoken || userData.languageSpoken.length === 0) {
                setShowLanguageSetupModal(true);
              }
              
              hasInitialized = true;
            }
          }
        }
      });

      return unsubscribeDoc;
    });

    return unsubscribeAuth;
  }, []); // Single auth listener, no redundant logic

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.pe-btn') && !event.target.closest('.pe-sidebar-role-dropdown')) {
        setShowRoleDropdown(false);
      }
      if (!event.target.closest('.pe-status-dropdown')) {
        setShowStatusDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const renderPanel = () => {
    switch (activePanel) {
      case "dashboard":
        return <PeerDashboard peerStatus={peerStatus} onPeerStatusChange={handlePeerStatusChange} showStatusDropdown={showStatusDropdown} setShowStatusDropdown={setShowStatusDropdown} />;
      case "chats":
        if (selectedTicket) {
          return (
            <PeerChatform
              chatId={selectedTicket.chatId}
              onBackToQueue={() => setSelectedTicket(null)}
            />
          );
        }
        return <PeerChats onOpenChat={setSelectedTicket} peerStatus={peerStatus} onPeerStatusChange={handlePeerStatusChange} showStatusDropdown={showStatusDropdown} setShowStatusDropdown={setShowStatusDropdown} onActiveChatsCountChange={setActiveChatsCount} />;
      case "resources":
        return <PeerResources />;
      case "profile":
        return <PeerProfile />;
      default:
        return <PeerDashboard peerStatus={peerStatus} onPeerStatusChange={handlePeerStatusChange} showStatusDropdown={showStatusDropdown} setShowStatusDropdown={setShowStatusDropdown} />;
    }
  };

  const handleLogout = () => {
    if (activeChatsCount > 0) {
      setShowCannotLogoutModal(true);
    } else {
      setIsLogoutModalOpen(true);
    }
  };

  // Called by StaffLogoutModal just before signing out — set status to Away
  const handleBeforeLogout = async () => {
    const user = auth.currentUser;
    if (!user) return;
    try {
      await updateDoc(doc(db, "users", user.uid), { peerStatus: "Away" });
    } catch (error) {
      console.error("PeerEducator: Failed to set peerStatus Away on logout:", error);
    }
  };

  // Language setup modal handlers
  const handleLanguageToggle = (lang) => {
    setLanguageSpoken(prev => prev.includes(lang) ? prev.filter(l => l !== lang) : [...prev, lang]);
  };

  // Handle adding a custom language
  const handleAddCustomLanguage = () => {
    const trimmed = tempNewLanguage.trim();
    if (!trimmed) {
      alert("Please enter a language");
      return;
    }
    if (!/^[a-zA-Z\s]+$/.test(trimmed)) {
      alert("Language should only contain letters");
      return;
    }
    if (trimmed.length > 15) {
      alert("Language cannot exceed 15 characters");
      return;
    }
    const lowerLang = trimmed.toLowerCase();
    if (!languageSpoken.includes(lowerLang) && !defaultLanguages.includes(lowerLang)) {
      setLanguageSpoken(prev => [...prev, lowerLang]);
    }
    setTempNewLanguage("");
  };

  const handlePeerStatusChange = async (newStatus) => {
    const user = auth.currentUser;
    if (!user) return;
    
    setPeerStatus(newStatus);
    setShowStatusDropdown(false);
    
    try {
      await updateDoc(doc(db, "users", user.uid), {
        peerStatus: newStatus
      });
    } catch (error) {
      console.error("Error updating peer status:", error);
    }
  };

  const handleApplyLanguages = async () => {
    const user = auth.currentUser;
    if (!user) return;

    if (languageSpoken.length === 0) {
      alert("Please select at least one language");
      return;
    }

    try {
      await updateDoc(doc(db, "users", user.uid), {
        languageSpoken: languageSpoken
      });
      setShowLanguageSetupModal(false);
    } catch (error) {
      console.error("Error saving languages:", error);
      alert("Failed to save languages. Please try again.");
    }
  };

  const handlePasswordVerificationRequired = (passwordData) => {
    setPendingPasswordChange(passwordData);
    setShowPasswordChangeModal(false); // Close password change modal
    setShowPasswordVerificationModal(true);
  };

  const handlePasswordVerificationSuccess = async (result) => {
    setShowPasswordVerificationModal(false);
    // Password change was successful, user will be signed out by the modal
    // Navigate to login page
    navigate('/login');
  };

  const handlePasswordVerificationCancel = () => {
    setShowPasswordVerificationModal(false);
    setShowPasswordChangeModal(true); // Reopen password change modal
  };

  return (
    <div className="pe-layout">
      {/* ── Custom Tooltip for Collapsed Sidebar ── */}
      {tooltip.visible && isDesktop && sidebarCollapsed && (
        <div
          className="pe-custom-tooltip"
          style={{
            left: tooltip.x,
            top: tooltip.y
          }}
        >
          {tooltip.text}
        </div>
      )}

      {/* ── Mobile backdrop ── */}
      {isMobileDrawerOpen && (
        <div
          className="pe-drawer-backdrop visible"
          onClick={() => setIsMobileDrawerOpen(false)}
        />
      )}

      {/* ================= Sidebar ================= */}
      <aside className={`pe-sidebar ${sidebarCollapsed ? 'collapsed' : ''} ${isMobileDrawerOpen ? 'drawer-open' : ''}`}>
        {/* Mobile sidebar header (compact navbar-style branding) */}
        <div className="pe-sidebar-mobile-header">
          <div className="pe-sidebar-mobile-logos">
            <img src={logoName} alt="AuraVit" />
            <span className="pe-sidebar-logo-sep">|</span>
            <img src={cfsiLogo} alt="CFSI" />
          </div>
          <button
            className="pe-drawer-close"
            onClick={() => setIsMobileDrawerOpen(false)}
            aria-label="Close menu"
          >
            <FiX />
          </button>
        </div>

        {/* Desktop brand section */}
        <div 
          className="pe-brand"
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
        >
          <img
            src={sidebarCollapsed ? auravitIcon : logoName}
            alt="AURAVIT"
            className="pe-sidebar-logo"
          />
          {!sidebarCollapsed && (
            <img
              src={cfsiLogo}
              alt="CFSI"
              className="pe-cfsi-logo"
            />
          )}
        </div>

        <div className="pe-sidebar-content">
          <nav className="pe-nav">
          <button
            className={activePanel === "dashboard" ? "active" : ""}
            onClick={() => { setActivePanel("dashboard"); setIsMobileDrawerOpen(false); }}
            onMouseEnter={(e) => handleMouseEnter(e, "Dashboard")}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            aria-label="Dashboard"
          >
            <FiGrid /> <span>Dashboard</span>
          </button>

          <button
            className={activePanel === "chats" ? "active" : ""}
            onClick={() => { setActivePanel("chats"); setIsMobileDrawerOpen(false); }}
            onMouseEnter={(e) => handleMouseEnter(e, "Chats")}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            aria-label="Chats"
          >
            <FiMessageCircle /> <span>Chats</span>
          </button>

          <button
            className={activePanel === "resources" ? "active" : ""}
            onClick={() => { setActivePanel("resources"); setIsMobileDrawerOpen(false); }}
            onMouseEnter={(e) => handleMouseEnter(e, "Resources")}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            aria-label="Resources"
          >
            <FiFolder /> <span>Resources</span>
          </button>

          <button
            className={activePanel === "profile" ? "active" : ""}
            onClick={() => { setActivePanel("profile"); setIsMobileDrawerOpen(false); }}
            onMouseEnter={(e) => handleMouseEnter(e, "Profile")}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            aria-label="Profile"
          >
            <FiUser /> <span>Profile</span>
          </button>

          <button
            className="pe-logout"
            onClick={handleLogout}
            onMouseEnter={(e) => handleMouseEnter(e, "Logout")}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            aria-label="Logout"
          >
            <FiLogOut /> <span>Logout</span>
          </button>
        </nav>
        </div>

        {/* ================= Sidebar Bottom Actions ================= */}
        <div className="pe-sidebar-bottom">
          <div className="pe-sidebar-bottom-row">
            {/* Switch Account Button */}
            <div className="pe-sidebar-switch">
              <button
                ref={switchButtonRef}
                className="pe-sidebar-switch-btn"
                onClick={handleDropdownToggle}
                onMouseEnter={(e) => handleMouseEnter(e, "Switch Account")}
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
                aria-label="Switch Account"
              >
                <HiOutlineSwitchVertical />
                <span>Switch Account</span>
                <FiChevronDown className={`pe-chevron ${showRoleDropdown ? 'open' : ''}`} />
              </button>

              {showRoleDropdown && (
                <div 
                  className="pe-sidebar-role-dropdown"
                  style={sidebarCollapsed && isDesktop ? {
                    position: 'fixed',
                    top: `${dropdownPosition.top}px`,
                    left: `${dropdownPosition.left}px`
                  } : {}}
                >
                  {availableRoles.length > 0 ? (
                    availableRoles.map((role) => (
                      <button
                        key={role.id}
                        className={`pe-sidebar-role-option ${role.id === currentUserRole ? "active" : ""}`}
                        onClick={() => handleRoleSwitch(role)}
                      >
                        {role.name}
                        {role.id === currentUserRole && <span className="pe-current-indicator">✓</span>}
                      </button>
                    ))
                  ) : (
                    <div className="pe-sidebar-role-option" style={{ color: '#999', cursor: 'default' }}>
                      No roles available
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </aside>

      {/* ── Mobile top header ── */}
      <header className="pe-mobile-header">
        <div className="pe-mobile-header-logos">
          <img src={logoName} alt="AuraVit" />
          <span className="pe-logo-sep">|</span>
          <img src={cfsiLogo} alt="CFSI" />
        </div>
        <button
          className="pe-mobile-hamburger"
          onClick={() => setIsMobileDrawerOpen(true)}
          aria-label="Open menu"
        >
          <FiMenu />
        </button>
      </header>

      {/* ================= Main ================= */}
      <section className="pe-main">
        <div className="pe-workspace">{renderPanel()}</div>
      </section>

      <StaffLogoutModal 
        isOpen={isLogoutModalOpen} 
        onClose={() => setIsLogoutModalOpen(false)}
        onBeforeLogout={handleBeforeLogout}
      />

      {/* Cannot Logout Modal — shown when there are active chats */}
      {showCannotLogoutModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(15, 23, 42, 0.85)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10001,
          padding: '20px'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '16px',
            overflow: 'hidden',
            maxWidth: '450px',
            width: '100%',
            boxShadow: '0 20px 40px rgba(15, 23, 42, 0.3)'
          }}>
            <div style={{
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              padding: '20px 24px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{
                margin: 0,
                color: '#fff',
                fontSize: '18px',
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <FiLogOut style={{ fontSize: '20px' }} /> Cannot Log Out
              </h3>
              <button
                type="button"
                onClick={() => setShowCannotLogoutModal(false)}
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  border: '1px solid rgba(255,255,255,0.25)',
                  borderRadius: '10px',
                  color: '#fff',
                  width: '36px',
                  height: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer'
                }}
              >
                <FiChevronDown style={{ fontSize: '18px', transform: 'rotate(-90deg)' }} />
              </button>
            </div>
            <div style={{ padding: '28px' }}>
              <p style={{
                margin: '0 0 8px 0',
                fontSize: '15px',
                lineHeight: '1.6',
                color: '#374151'
              }}>
                You have <strong>{activeChatsCount} active {activeChatsCount === 1 ? 'chat' : 'chats'}</strong> in progress.
              </p>
              <p style={{
                margin: '0 0 24px 0',
                fontSize: '15px',
                lineHeight: '1.6',
                color: '#374151'
              }}>
                Please complete or resolve all active chats before logging out.
              </p>
              <button
                onClick={() => setShowCannotLogoutModal(false)}
                style={{
                  width: '100%',
                  padding: '12px 24px',
                  background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 6px rgba(30, 58, 138, 0.2)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #1e40af 0%, #60a5fa 100%)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 12px rgba(30, 58, 138, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(30, 58, 138, 0.2)';
                }}
              >
                I Understand
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mandatory Password Change Modal */}
      {showPasswordChangeModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(15, 23, 42, 0.85)',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '20px'
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '16px',
            overflow: 'hidden',
            maxWidth: '600px',
            width: '100%',
            boxShadow: '0 25px 65px rgba(15, 23, 42, 0.45)',
            animation: 'scpModalFadeIn 0.25s cubic-bezier(0.4, 0, 0.2, 1)'
          }}>
            <StaffChangePassword userData={userData} isMandatory={true} onPasswordVerificationRequired={handlePasswordVerificationRequired} />
          </div>
        </div>
      )}

      {/* Password Verification Modal */}
      <StaffChangeVerificationModal
        isOpen={showPasswordVerificationModal}
        onClose={() => setShowPasswordVerificationModal(false)}
        flow="password-change"
        pendingPasswordChange={pendingPasswordChange}
        onSuccess={handlePasswordVerificationSuccess}
        onCancel={handlePasswordVerificationCancel}
      />

      {/* Mandatory Language Setup Modal - Refined with System Branding */}
      {showLanguageSetupModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(15, 23, 42, 0.85)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '20px'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '16px',
            overflow: 'hidden',
            maxWidth: '550px',
            width: '100%',
            boxShadow: '0 20px 40px rgba(15, 23, 42, 0.3)'
          }}>
            {/* Header with System Branding */}
            <div style={{ 
              background: 'linear-gradient(135deg, #1e3a8a 0%, #0f172a 100%)',
              padding: '20px 24px'
            }}>
              <h3 style={{ 
                margin: 0, 
                color: '#fff', 
                fontSize: '18px', 
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                As a Peer Educator, you will communicate with users. What languages are you fluent in?
              </h3>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '28px' }}>
              {/* Language Options */}
              <div style={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: '10px', 
                marginBottom: '24px'
              }}>
                {/* Default languages */}
                {defaultLanguages.map((lang) => {
                  let label;
                  if (lang === "english") label = "English";
                  else if (lang === "tagalog") label = "Tagalog";
                  else if (lang === "bisaya") label = "Bisaya";
                  
                  return (
                    <button
                      key={lang}
                      onClick={() => handleLanguageToggle(lang)}
                      style={{
                        padding: '10px 20px',
                        borderRadius: '999px',
                        border: languageSpoken.includes(lang) 
                          ? '2px solid #3f62b5' 
                          : '1px solid #e2e8f0',
                        background: languageSpoken.includes(lang) 
                          ? 'linear-gradient(135deg, #3f62b5 0%, #2563eb 100%)' 
                          : '#fff',
                        color: languageSpoken.includes(lang) ? '#fff' : '#475569',
                        fontSize: '14px',
                        fontWeight: 700,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        boxShadow: languageSpoken.includes(lang) 
                          ? '0 4px 12px rgba(63, 98, 181, 0.3)' 
                          : 'none'
                      }}
                      onMouseEnter={(e) => {
                        if (!languageSpoken.includes(lang)) {
                          e.target.style.borderColor = '#3f62b5';
                          e.target.style.color = '#3f62b5';
                          e.target.style.background = '#eff6ff';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!languageSpoken.includes(lang)) {
                          e.target.style.borderColor = '#e2e8f0';
                          e.target.style.color = '#475569';
                          e.target.style.background = '#fff';
                        }
                      }}
                    >
                      {label}
                    </button>
                  );
                })}
                {/* Custom languages */}
                {languageSpoken.filter(lang => !defaultLanguages.includes(lang)).map((lang) => (
                  <button
                    key={lang}
                    onClick={() => handleLanguageToggle(lang)}
                    style={{
                      padding: '10px 20px',
                      borderRadius: '999px',
                      border: languageSpoken.includes(lang) 
                        ? '2px solid #3f62b5' 
                        : '1px solid #e2e8f0',
                      background: languageSpoken.includes(lang) 
                        ? 'linear-gradient(135deg, #3f62b5 0%, #2563eb 100%)' 
                        : '#fff',
                      color: languageSpoken.includes(lang) ? '#fff' : '#475569',
                      fontSize: '14px',
                      fontWeight: 700,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      boxShadow: languageSpoken.includes(lang) 
                        ? '0 4px 12px rgba(63, 98, 181, 0.3)' 
                        : 'none'
                    }}
                    onMouseEnter={(e) => {
                      if (!languageSpoken.includes(lang)) {
                        e.target.style.borderColor = '#3f62b5';
                        e.target.style.color = '#3f62b5';
                        e.target.style.background = '#eff6ff';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!languageSpoken.includes(lang)) {
                        e.target.style.borderColor = '#e2e8f0';
                        e.target.style.color = '#475569';
                        e.target.style.background = '#fff';
                      }
                    }}
                  >
                    {lang.charAt(0).toUpperCase() + lang.slice(1)}
                  </button>
                ))}
              </div>

              {/* Add custom language */}
              <div style={{ marginBottom: '24px' }}>
                <div style={{ 
                  display: 'flex', 
                  gap: '10px'
                }}>
                  <input
                    type="text"
                    placeholder="Add a language..."
                    value={tempNewLanguage}
                    onChange={(e) => setTempNewLanguage(e.target.value)}
                    maxLength={15}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddCustomLanguage()}
                    style={{
                      flex: 1,
                      padding: '12px 16px',
                      borderRadius: '10px',
                      border: '1px solid #e2e8f0',
                      fontSize: '15px',
                      background: '#f8fafc',
                      color: '#1e293b',
                      outline: 'none',
                      transition: 'all 0.2s'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#3f62b5';
                      e.target.style.boxShadow = '0 0 0 3px rgba(63, 98, 181, 0.1)';
                      e.target.style.background = '#fff';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e2e8f0';
                      e.target.style.boxShadow = 'none';
                      e.target.style.background = '#f8fafc';
                    }}
                  />
                  <button
                    onClick={handleAddCustomLanguage}
                    disabled={!tempNewLanguage.trim()}
                    style={{
                      padding: '12px 24px',
                      background: tempNewLanguage.trim() 
                        ? 'linear-gradient(135deg, #3f62b5 0%, #2563eb 100%)' 
                        : '#cbd5e1',
                      color: tempNewLanguage.trim() ? '#fff' : '#94a3b8',
                      fontSize: '14px',
                      fontWeight: 700,
                      borderRadius: '10px',
                      border: 'none',
                      cursor: tempNewLanguage.trim() ? 'pointer' : 'not-allowed',
                      boxShadow: tempNewLanguage.trim() ? '0 4px 12px rgba(63, 98, 181, 0.3)' : 'none',
                      transition: 'all 0.2s'
                    }}
                  >
                    Add
                  </button>
                </div>
              </div>

              {/* Button */}
              <div>
                <button 
                  onClick={handleApplyLanguages}
                  disabled={languageSpoken.length === 0}
                  style={{
                    width: '100%',
                    padding: '12px 24px',
                    background: languageSpoken.length > 0 
                      ? 'linear-gradient(135deg, #3f62b5 0%, #2563eb 100%)' 
                      : '#cbd5e1',
                    color: languageSpoken.length > 0 ? '#fff' : '#94a3b8',
                    fontSize: '14px',
                    fontWeight: 700,
                    borderRadius: '10px',
                    border: 'none',
                    cursor: languageSpoken.length > 0 ? 'pointer' : 'not-allowed',
                    boxShadow: languageSpoken.length > 0 ? '0 4px 12px rgba(63, 98, 181, 0.3)' : 'none',
                    transition: 'all 0.2s'
                  }}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PeerEducator;
